'use client';

import { useState, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useOrganization } from '@/lib/platform/contexts/organization-context';
import { useSession, stopImpersonating } from '@/lib/platform/auth/client';
import { useInvitationPolling } from '@/lib/platform/hooks/use-invitation-polling';
import { useOrganizationSelection } from '@/lib/platform/hooks/use-organization-preferences';
import { useTabs } from '@/lib/platform/contexts/tabs-context';
import TailwindSidebar from '@/components/platform/navigation/TailwindSidebar';
import TailwindHeader from '@/components/platform/navigation/TailwindHeader';
import { TabNavigation } from '@/components/platform/ui';
import Spinner from '@/components/platform/ui/Spinner';
import SubscriptionBanner from './SubscriptionBanner';
import { SectionBottomMenu } from '@/components/platform/navigation';
import { AiAssistantProvider } from '@/lib/platform/contexts/ai-assistant-context';
import { AiFormBridgeProvider } from '@/lib/platform/contexts/ai-form-bridge-context';
import { AiOrb, AiAssistantPanel, AiWorkingIndicator } from '@/components/platform/ai';
import SetupWizardController from '@/components/platform/onboarding/SetupWizardController';
import { logger } from '@/lib/platform/utils/logger';

export default function LayoutWrapper({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { darkMode } = useDarkMode();
  const { organization, isLoadingOrganization } = useOrganization();
  const { data: session, isPending, refetch } = useSession();
  const { setSelectedOrganization } = useOrganizationSelection();
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const { tabs, activeTab, onTabChange } = useTabs();

  // Poll for pending invitations (shows toast notifications)
  useInvitationPolling();

  // Ensure page starts at top on navigation (fixes mobile viewport issue)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Check if user is impersonating
  // Try both possible locations for impersonatedBy
  const sessionRecord = session as Record<string, unknown>;
  const sessionNested = sessionRecord?.session as Record<string, unknown> | undefined;
  const impersonatedByNested = sessionNested?.impersonatedBy;
  const impersonatedByDirect = sessionRecord?.impersonatedBy;

  // Failsafe: If impersonatedBy is set but user email is the admin email (jeppe@blackbook.dk),
  // it means the session cache is stale and should not show banner
  const adminEmail = 'jeppe@blackbook.dk';
  const currentUserEmail = session?.user?.email;
  // Check if impersonatedBy has a truthy value (not null, undefined, or false)
  const hasImpersonationFlag = !!impersonatedByNested || !!impersonatedByDirect;
  const isStaleImpersonation = hasImpersonationFlag && currentUserEmail === adminEmail;

  const isImpersonating = hasImpersonationFlag && !isStaleImpersonation;

  // When impersonating, session.user is the impersonated user
  const impersonatedUserEmail = session?.user?.email || 'Unknown User';

  // Check page type for conditional redirects and navigation display
  const isAuthPage = pathname?.startsWith('/auth') || pathname?.startsWith('/invite');
  const isRootPage = pathname === '/';
  const isWelcomePage = pathname === '/welcome';
  const isAdminPage = pathname?.startsWith('/admin');
  const isUsersPage = pathname === '/users';
  const isProfilePage = pathname === '/profile';
  const isPublicProposalPage = pathname?.startsWith('/proposal/');
  const isProposalPreviewPage = pathname?.includes('/packages/') && pathname?.endsWith('/preview');
  const isTrustCenterPage = pathname?.startsWith('/trust-center');
  // FIX: Check sessionStorage for onboarding completion flag (prevents redirect loop during onboarding)
  const fromOnboarding =
    typeof window !== 'undefined' && sessionStorage.getItem('onboardingJustCompleted') === 'true';

  // Check for stale impersonation state on mount and periodically
  useEffect(() => {
    const checkStaleImpersonation = async () => {
      // If we detect stale impersonation, force refetch to clear cache
      if (isStaleImpersonation) {
        logger.log('[LayoutWrapper] Detected stale impersonation state, forcing refetch...');
        if (typeof refetch === 'function') {
          await refetch();
        }
      }
    };

    checkStaleImpersonation();
  }, [isStaleImpersonation, refetch]);

  // FIX: Clear onboarding and demo signup flags once organization loads successfully
  useEffect(() => {
    if (organization && !isLoadingOrganization && typeof window !== 'undefined') {
      if (fromOnboarding) {
        logger.log('[LayoutWrapper] Organization loaded, clearing onboarding flag');
        sessionStorage.removeItem('onboardingJustCompleted');
      }

      // CRITICAL FIX: Also clear demo signup flag to prevent redirect loop
      const demoSignupInProgress = sessionStorage.getItem('demoSignupInProgress') === 'true';
      if (demoSignupInProgress) {
        logger.log('[LayoutWrapper] Organization loaded, clearing demo signup flag');
        sessionStorage.removeItem('demoSignupInProgress');
      }
    }
  }, [fromOnboarding, organization, isLoadingOrganization]);

  // Redirect to welcome page if no organization exists (only for authenticated users, excluding admin/users/welcome/profile/auth pages)
  useEffect(() => {
    // CRITICAL FIX: Check if session has activeOrganizationId (means org exists, just loading)
    const sessionRecord = session as Record<string, unknown>;
    const sessionNested = sessionRecord?.session as Record<string, unknown> | undefined;
    const hasActiveOrgId = !!sessionNested?.activeOrganizationId;

    logger.log('[LayoutWrapper] Redirect check:', {
      organization:
        organization === null ? 'null' : organization === undefined ? 'undefined' : 'exists',
      isLoadingOrganization,
      hasSession: !!session?.user,
      isPending,
      pathname,
      isAdminPage,
      isWelcomePage,
      isAuthPage,
      isRootPage,
      isUsersPage,
      isProfilePage,
      fromOnboarding,
      hasActiveOrgId,
      shouldRedirect:
        !organization &&
        !isLoadingOrganization &&
        !hasActiveOrgId &&
        session?.user &&
        !isPending &&
        !isAdminPage &&
        !isWelcomePage &&
        !isAuthPage &&
        !isUsersPage &&
        !isProfilePage &&
        !fromOnboarding,
    });

    // CRITICAL FIX: Don't redirect if session has activeOrganizationId
    // This means the user has an organization, it's just still loading
    if (
      !organization &&
      !isLoadingOrganization &&
      !hasActiveOrgId &&
      session?.user &&
      !isPending &&
      !isAdminPage &&
      !isWelcomePage &&
      !isAuthPage &&
      !isUsersPage &&
      !isProfilePage &&
      !fromOnboarding
    ) {
      logger.log('[LayoutWrapper] REDIRECTING to /welcome from:', pathname);
      router.push('/welcome');
    }
  }, [
    organization,
    isLoadingOrganization,
    session,
    isPending,
    isAdminPage,
    isWelcomePage,
    isAuthPage,
    isRootPage,
    isUsersPage,
    isProfilePage,
    fromOnboarding,
    router,
    pathname,
  ]);

  // Redirect to login if session loading is done and user is not logged in (except public pages)
  useEffect(() => {
    if (
      !isPending &&
      !session?.user &&
      !isAuthPage &&
      !isPublicProposalPage &&
      !isTrustCenterPage
    ) {
      logger.log('[LayoutWrapper] User not logged in, redirecting to login');
      router.push('/auth/login');
    }
  }, [isPending, session, router, isAuthPage, isPublicProposalPage, isTrustCenterPage]);

  logger.log('[LayoutWrapper] === Session Debug ===');
  logger.log('[LayoutWrapper] session.user.email:', session?.user?.email);
  logger.log('[LayoutWrapper] impersonatedByNested:', impersonatedByNested);
  logger.log('[LayoutWrapper] impersonatedByDirect:', impersonatedByDirect);
  logger.log('[LayoutWrapper] hasImpersonationFlag:', hasImpersonationFlag);
  logger.log('[LayoutWrapper] currentUserEmail:', currentUserEmail);
  logger.log('[LayoutWrapper] isStaleImpersonation:', isStaleImpersonation);
  logger.log('[LayoutWrapper] isImpersonating (final):', isImpersonating);
  logger.log('[LayoutWrapper] Impersonated user email:', impersonatedUserEmail);
  logger.log('[LayoutWrapper] ==================');

  const handleStopImpersonating = async () => {
    logger.log('[LayoutWrapper] === Stop Impersonation Start ===');
    logger.log('[LayoutWrapper] Current session.user.email before stop:', session?.user?.email);

    setStoppingImpersonation(true);
    try {
      logger.log('[LayoutWrapper] Calling stopImpersonating()...');
      const result = await stopImpersonating();
      logger.log('[LayoutWrapper] Stop impersonation result:', JSON.stringify(result, null, 2));

      if (result?.error) {
        logger.error('[LayoutWrapper] Stop impersonation returned error:', result.error);

        // If error is "YOU_ARE_NOT_IMPERSONATING_ANYONE", just refresh to clear stale session
        if (result.error.code === 'YOU_ARE_NOT_IMPERSONATING_ANYONE') {
          logger.log('[LayoutWrapper] Impersonation already stopped, clearing stale session...');

          // Clear selected organization
          setSelectedOrganization(null);
          logger.log('[LayoutWrapper] Cleared selected organization');

          // Force refetch to clear cache
          if (typeof refetch === 'function') {
            await refetch();
          }
          // Intentional full-page reload to clear any remaining state after impersonation
          // eslint-disable-next-line @next/next/no-location-assign-relative-destination
          window.location.href = '/admin/teams';
          return;
        }

        alert(`Failed to stop impersonating: ${result.error.message || 'Unknown error'}`);
        setStoppingImpersonation(false);
        return;
      }

      logger.log('[LayoutWrapper] Successfully stopped impersonation');

      // Clear selected organization to prevent "Access denied" errors
      if (typeof window !== 'undefined') {
        setSelectedOrganization(null);
        logger.log('[LayoutWrapper] Cleared selected organization');
      }

      // Force refetch session to clear cache
      if (typeof refetch === 'function') {
        logger.log('[LayoutWrapper] Refetching session...');
        await refetch();
      }

      // Wait a bit then force a hard reload to ensure everything is cleared
      logger.log('[LayoutWrapper] Reloading page...');
      setTimeout(() => {
        // eslint-disable-next-line @next/next/no-location-assign-relative-destination
        window.location.href = '/admin/teams';
      }, 200);
    } catch (error) {
      logger.error('[LayoutWrapper] Exception stopping impersonation:', error);
      alert(
        `Failed to stop impersonating: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      setStoppingImpersonation(false);
    }
  };

  // Auth pages and root page render without any layout
  if (isAuthPage || isRootPage) {
    logger.log('[LayoutWrapper] Rendering auth/root page - no banner');
    return <>{children}</>;
  }

  // Trust Center pages render without any layout (public, no authentication required)
  if (isTrustCenterPage) {
    logger.log('[LayoutWrapper] Rendering trust center page - no layout');
    return <>{children}</>;
  }

  // Public proposal pages render without any layout (no authentication required)
  if (isPublicProposalPage) {
    logger.log('[LayoutWrapper] Rendering public proposal page - no layout');
    return <>{children}</>;
  }

  // Admin pages render with simple header layout (no sidebar, no club context required)
  if (isAdminPage) {
    logger.log('[LayoutWrapper] Rendering admin page - no club context required');
    return (
      <div className={`flex h-screen flex-col ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        {/* Banner for impersonation or back to dashboard */}
        <div className="shrink-0 print:hidden">
          {isImpersonating ? (
            <SubscriptionBanner
              status="trial"
              isImpersonating={isImpersonating}
              impersonatedUserEmail={impersonatedUserEmail}
              onStopImpersonating={handleStopImpersonating}
              stoppingImpersonation={stoppingImpersonation}
            />
          ) : (
            <div className={`w-full ${darkMode ? 'bg-indigo-600' : 'bg-indigo-500'}`}>
              <div className="flex items-center gap-x-6 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
                <p className="flex items-center text-sm/6 text-white">
                  <strong className="font-semibold">Super Admin Panel</strong>
                </p>
                <div className="flex flex-1 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setNavigating(true);
                      router.push('/dashboard');
                    }}
                    disabled={navigating}
                    className="rounded-md bg-white/20 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-white/30 disabled:opacity-50"
                  >
                    {navigating ? 'Loading...' : 'Back to Dashboard'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 scroll-pt-16 overflow-y-auto scroll-smooth">
          <TailwindHeader setSidebarOpen={setSidebarOpen} hideMenuButton />
          <main>
            <div className="min-h-[calc(100vh-4rem)] px-4 pt-5 pb-5 sm:px-6 lg:px-8">
              {children}
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Welcome page renders with simple header layout (no sidebar, no topbar)
  if (isWelcomePage) {
    logger.log('[LayoutWrapper] Rendering welcome page without topbar');

    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <TailwindHeader setSidebarOpen={setSidebarOpen} hideMenuButton />
        <main className="scroll-pt-16 scroll-smooth">
          <div className="min-h-[calc(100vh-4rem)]">{children}</div>
        </main>
      </div>
    );
  }

  // Profile page during onboarding (no organization) renders with simple header layout (no sidebar)
  if (isProfilePage && !organization && !isLoadingOrganization) {
    logger.log('[LayoutWrapper] Rendering profile page during onboarding - NO SIDEBAR');
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <TailwindHeader setSidebarOpen={setSidebarOpen} hideMenuButton />
        <main className="scroll-pt-16 scroll-smooth">
          <div className="min-h-[calc(100vh-4rem)] px-4 pt-5 pb-5 sm:px-6 lg:px-8">{children}</div>
        </main>
      </div>
    );
  }

  // COMBINED loading state - show spinner ONLY when:
  // 1. Auth is loading (isPending) OR
  // 2. Organization is loading (isLoadingOrganization OR organization === undefined) OR
  // 3. Redirecting to welcome (no organization found)
  // This prevents multiple spinner flashes
  const isLoading =
    isPending ||
    isLoadingOrganization ||
    organization === undefined ||
    (!organization &&
      !isAdminPage &&
      !isWelcomePage &&
      !isAuthPage &&
      !isRootPage &&
      !isUsersPage &&
      !isProfilePage);

  // Show single unified loading state (prevents multiple re-renders)
  if (isLoading && !isPublicProposalPage) {
    return (
      <div
        className={`flex min-h-screen items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}
      >
        <Spinner size="lg" darkMode={darkMode} />
      </div>
    );
  }

  // Redirect to login after loading completes (if no session)
  if (!session?.user && !isPublicProposalPage) {
    return null; // Return null briefly before redirect
  }

  // Show full layout with navigation when organization exists OR on admin/welcome pages
  logger.log('[LayoutWrapper] Rendering full layout - WITH BANNER (full width)');

  const isFullPageScroll = pathname?.startsWith('/plugins') || pathname?.startsWith('/welcome');

  if (isFullPageScroll) {
    // Completely different structure for pages that need sticky header with full page scroll
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        {/* Banner */}
        <div className="print:hidden">
          <SubscriptionBanner
            status="trial"
            isImpersonating={isImpersonating}
            impersonatedUserEmail={impersonatedUserEmail}
            onStopImpersonating={handleStopImpersonating}
            stoppingImpersonation={stoppingImpersonation}
          />
        </div>

        {/* Desktop sidebar for plugins only - fixed position */}
        {pathname?.startsWith('/plugins') && (
          <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col print:hidden">
            <TailwindSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
          </div>
        )}

        {/* Mobile sidebar */}
        {!pathname?.startsWith('/welcome') && (
          <div className="lg:hidden print:hidden">
            <TailwindSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
          </div>
        )}

        {/* Main content area with left padding for sidebar */}
        <div className={pathname?.startsWith('/welcome') ? '' : 'lg:pl-72'}>
          {/* Sticky header - sticky is on TailwindHeader itself */}
          <div className="print:hidden">
            <TailwindHeader setSidebarOpen={setSidebarOpen} />
          </div>

          {/* Main content */}
          <main>
            <div className="px-4 pt-5 pb-24 sm:px-6 md:pb-5 lg:px-8">{children}</div>
          </main>

          {/* Bottom Mobile Menu */}
          <SectionBottomMenu />
        </div>
      </div>
    );
  }

  // AI assistant: available to all logged-in users (server enforces org
  // membership per request and a daily message quota)
  const aiAssistantEnabled = !!session?.user;

  // Standard layout for other pages (fixed header, scrolling main)
  return (
    <AiAssistantProvider>
      <AiFormBridgeProvider>
        <div className={`flex h-screen flex-col ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          {/* Banner */}
          <div className="shrink-0 print:hidden">
            <SubscriptionBanner
              status="trial"
              isImpersonating={isImpersonating}
              impersonatedUserEmail={impersonatedUserEmail}
              onStopImpersonating={handleStopImpersonating}
              stoppingImpersonation={stoppingImpersonation}
            />
          </div>

          {/* Main content area */}
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <div className="hidden lg:block lg:w-72 lg:shrink-0 print:hidden">
              <TailwindSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
            </div>
            <div className="lg:hidden print:hidden">
              <TailwindSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
            </div>

            {/* Content with fixed header and scrolling main */}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="shrink-0 print:hidden">
                <TailwindHeader setSidebarOpen={setSidebarOpen} />
              </div>

              {/* Render tabs between header and main if they exist */}
              {tabs && tabs.length > 0 && activeTab && onTabChange && (
                <div className="shrink-0">
                  <TabNavigation tabs={tabs} activeTab={activeTab} onChange={onTabChange} />
                </div>
              )}

              <main className="flex-1 scroll-pt-16 overflow-y-auto scroll-smooth">
                <div
                  className={
                    isProposalPreviewPage
                      ? 'h-full'
                      : tabs && tabs.length > 0
                        ? 'min-h-[calc(100vh-4rem)] px-4 pb-24 sm:px-6 md:pb-5 lg:px-8'
                        : 'min-h-[calc(100vh-4rem)] px-4 pt-5 pb-24 sm:px-6 md:pb-5 lg:px-8'
                  }
                >
                  {children}
                </div>
              </main>

              {/* Bottom Mobile Menu — shifted left when the AI orb is shown */}
              <SectionBottomMenu shiftLeftForOrb={aiAssistantEnabled} />
            </div>

            {/* AI assistant panel — in-flow sibling so it pushes content (desktop) */}
            {aiAssistantEnabled && <AiAssistantPanel />}
          </div>
        </div>
        {aiAssistantEnabled && <AiOrb />}
        {aiAssistantEnabled && <AiWorkingIndicator />}
        {/* Post org-creation setup wizard (auto-opens for new orgs) */}
        <SetupWizardController />
      </AiFormBridgeProvider>
    </AiAssistantProvider>
  );
}
